// src/pages/user/ProjectsPage.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Grid, List, Search, Trash2, Upload, Plus, FolderOpen, Calendar,
  Filter, RefreshCw, Eye, Crown, Zap, Image as ImageIcon, UserPlus, Send, Loader, X, Lock, Copy, Edit2,
} from "lucide-react";
import ConfirmModal from "../../components/ConfirmModal";
import TooltipButton2 from "../../components/TooltipButton2";
import { HiCurrencyDollar } from "react-icons/hi2";
import { FaTelegramPlane } from "react-icons/fa";
import { MdDownload } from "react-icons/md";
import { useEditorLink } from "../../utils/useEditorLink";
import { useToast } from "../../components/ToastProvider";
import { injectUserStyles, statusBadgeClass, fmtStatus } from "./userStyles";

const BASE = process.env.REACT_APP_BACKEND_URL || "";

const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];

/* ── Style injection ── */
const STYLE_ID = "projects-page-styles";
function injectLocal() {
  injectUserStyles();
  if (document.getElementById(STYLE_ID)) return;
  const el = document.createElement("style");
  el.id = STYLE_ID;
  el.textContent = `
    .pp-controls { background:#fff; border:1px solid #e8ecf0; border-radius:10px; padding:16px 20px; margin-bottom:20px; }
    .pp-controls-row { display:flex; flex-wrap:wrap; gap:10px; align-items:center; }
    .pp-search-wrap { position:relative; flex:1; min-width:180px; max-width:360px; }
    .pp-search-icon { position:absolute; left:10px; top:50%; transform:translateY(-50%); color:#9ca3af; pointer-events:none; }
    .pp-input {
      width:100%; padding:8px 12px 8px 34px; box-sizing:border-box;
      border:1px solid #e2e8f0; border-radius:7px; font-size:13px;
      outline:none; transition:border-color 150ms; font-family:'DM Sans',sans-serif;
    }
    .pp-input:focus { border-color:#f9ab14; }
    .pp-filter-wrap { position:relative; }
    .pp-filter-icon { position:absolute; left:10px; top:50%; transform:translateY(-50%); color:#9ca3af; pointer-events:none; }
    .pp-select {
      padding:8px 12px 8px 32px; border:1px solid #e2e8f0; border-radius:7px;
      font-size:13px; background:#fff; outline:none; font-family:'DM Sans',sans-serif;
      min-width:130px; transition:border-color 150ms;
    }
    .pp-select:focus { border-color:#f9ab14; }
    .pp-view-toggle { display:inline-flex; border:1px solid #e2e8f0; border-radius:7px; overflow:hidden; }
    .pp-view-btn { padding:7px 10px; border:none; cursor:pointer; background:transparent; transition:background 150ms; }
    .pp-view-btn.active { background:#0c1118; color:#fff; }
    .pp-view-btn:not(.active) { color:#9ca3af; }
    .pp-view-btn:not(.active):hover { background:#f3f4f6; color:#374151; }
    .pp-usage-banner { border-radius:10px; padding:14px 18px; margin-bottom:20px; display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:12px; }
    .pp-card-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(240px,1fr)); gap:16px; }
    .pp-card { background:#fff; border:1px solid #e8ecf0; border-radius:10px; overflow:hidden; transition:box-shadow 200ms; }
    .pp-card:hover { box-shadow:0 4px 16px rgba(0,0,0,.08); }
    .pp-card-thumb { position:relative; aspect-ratio:16/9; background:#f4f5f7; display:flex; align-items:center; justify-content:center; overflow:hidden; }
    .pp-card-thumb img { width:100%; height:100%; object-fit:cover; }
    .pp-card-body { padding:14px; }
    .pp-card-name { font-size:13.5px; font-weight:600; color:#0c1118; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; margin-bottom:2px; }
    .pp-card-addr { font-size:11.5px; color:#9ca3af; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; margin-bottom:2px; }
    .pp-card-date { font-size:11px; color:#9ca3af; }
    .pp-card-actions { display:flex; gap:4px; align-items:center; flex-shrink:0; }
    .pp-open-btn { flex:1; padding:8px; border-radius:7px; border:none; background:#0c1118; color:#fff; font-size:13px; font-weight:500; cursor:pointer; transition:background 150ms; font-family:'DM Sans',sans-serif; }
    .pp-open-btn:hover { background:#1e2832; }
    .pp-open-btn:disabled { background:#d1d5db; color:#9ca3af; cursor:not-allowed; }
    .pp-table-card { background:#fff; border:1px solid #e8ecf0; border-radius:10px; overflow:hidden; }
    .pp-table { width:100%; border-collapse:collapse; font-size:13px; }
    .pp-table thead tr { background:#f9fafb; border-bottom:1px solid #e8ecf0; }
    .pp-table th { padding:10px 14px; text-align:left; font-family:'DM Sans',sans-serif; font-size:9.5px; letter-spacing:.04em; color:#9ca3af; white-space:nowrap; }
    .pp-table td { padding:12px 14px; border-bottom:1px solid #f3f4f6; vertical-align:middle; }
    .pp-table tbody tr:last-child td { border-bottom:none; }
    .pp-table tbody tr:hover td { background:#fafafa; }
    .pp-proj-name { font-size:13.5px; font-weight:500; color:#0c1118; }
    .pp-proj-addr { font-size:11px; color:#9ca3af; }
    .pp-proj-id   { font-family:'DM Sans',sans-serif; font-size:10px; color:#d1d5db; }
    .pp-pill { display:inline-flex; align-items:center; padding:2px 8px; border-radius:20px; font-family:'DM Sans',sans-serif; font-size:9.5px; letter-spacing:.05em; font-weight:500; }
    .pp-pill-plan  { background:rgba(99,102,241,.1);  color:#3730a3; }
    .pp-pill-quote { background:rgba(16,185,129,.1);  color:#065f46; }
    .pp-pill-lock  { background:rgba(239,68,68,.1);   color:#991b1b; }
    .pp-icon-btn { padding:6px; border:none; background:none; cursor:pointer; color:#9ca3af; border-radius:6px; transition:background 150ms, color 150ms; display:inline-flex; align-items:center; }
    .pp-icon-btn:hover:not(:disabled) { background:#f3f4f6; color:#374151; }
    .pp-icon-btn:disabled { opacity:.35; cursor:not-allowed; }
    /* Quote dialog */
    .pp-dialog-backdrop { position:fixed; inset:0; z-index:999; background:rgba(0,0,0,.55); display:flex; align-items:center; justify-content:center; padding:20px; backdrop-filter:blur(3px); }
    .pp-dialog { background:#fff; border-radius:12px; width:100%; max-width:520px; overflow:hidden; box-shadow:0 24px 64px rgba(0,0,0,.2); }
    .pp-dialog-head { display:flex; align-items:center; justify-content:space-between; padding:18px 22px 14px; border-bottom:1px solid #f3f4f6; }
    .pp-dialog-title { font-size:15px; font-weight:600; color:#0c1118; }
    .pp-dialog-body { padding:20px 22px; max-height:68vh; overflow-y:auto; display:flex; flex-direction:column; gap:14px; }
    .pp-dialog-foot { padding:14px 22px; border-top:1px solid #f3f4f6; display:flex; justify-content:flex-end; gap:10px; }
    .pp-form-grid-2 { display:grid; grid-template-columns:1fr 1fr; gap:12px; }
    .pp-form-grid-5 { display:grid; grid-template-columns:2fr 2fr 1fr; gap:12px; }
    .pp-field-label { font-family:'DM Sans',sans-serif; font-size:10px; letter-spacing:.04em; color:#6b7280; display:block; margin-bottom:5px; }
    .pp-field-input {
      width:100%; box-sizing:border-box; padding:8px 11px;
      border:1px solid #e2e8f0; border-radius:7px; font-size:13.5px;
      color:#0c1118; outline:none; transition:border-color 150ms, box-shadow 150ms;
      font-family:'DM Sans',sans-serif;
    }
    .pp-field-input:focus { border-color:#f9ab14; box-shadow:0 0 0 3px rgba(249,171,20,.1); }
    .pp-field-input.error { border-color:#ef4444; }
    .pp-field-error { font-size:11px; color:#ef4444; margin-top:3px; }
    .pp-textarea { min-height:80px; resize:vertical; }
    /* Assign modal */
    .pp-modal-backdrop { position:fixed; inset:0; z-index:50; background:rgba(0,0,0,.45); display:flex; align-items:center; justify-content:center; padding:20px; }
    .pp-modal { background:#fff; border-radius:10px; padding:24px; width:100%; max-width:420px; box-shadow:0 12px 40px rgba(0,0,0,.15); }
    .pp-modal-title { font-size:15px; font-weight:600; color:#0c1118; margin-bottom:16px; display:flex; align-items:center; gap:8px; }
  `;
  document.head.appendChild(el);
}

/* ── Quote Request Dialog ── */
const EMPTY_FORM = { firstName:"", lastName:"", email:"", phone:"", address:"", city:"", state:"", zip:"", building:"" };

function QuoteRequestDialog({ open, onClose, onSubmit, submitting }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});

  useEffect(() => { if (open) { setForm(EMPTY_FORM); setErrors({}); } }, [open]);
  if (!open) return null;

  function set(field, value) { setForm(f => ({ ...f, [field]: value })); setErrors(e => ({ ...e, [field]: undefined })); }

  function validate() {
    const e = {};
    if (!form.firstName.trim()) e.firstName = "Required";
    if (!form.lastName.trim())  e.lastName  = "Required";
    if (!form.email.trim())     e.email     = "Required";
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = "Invalid email";
    if (!form.phone.trim())     e.phone     = "Required";
    if (!form.address.trim())   e.address   = "Required";
    if (!form.city.trim())      e.city      = "Required";
    if (!form.state)            e.state     = "Required";
    if (!form.zip.trim())       e.zip       = "Required";
    return e;
  }

  function handleSubmit(e) {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    onSubmit(form);
  }

  function F({ field, label, type = "text", placeholder, autoFocus = false }) {
    return (
      <div>
        <label className="pp-field-label">{label}</label>
        <input type={type} placeholder={placeholder} value={form[field]} onChange={e => set(field, e.target.value)}
          autoFocus={autoFocus} className={`pp-field-input ${errors[field] ? "error" : ""}`} />
        {errors[field] && <div className="pp-field-error">{errors[field]}</div>}
      </div>
    );
  }

  return (
    <div className="pp-dialog-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="pp-dialog">
        <div className="pp-dialog-head">
          <div className="pp-dialog-title">Request Quote</div>
          <button onClick={onClose} style={{ background:"none", border:"none", cursor:"pointer", color:"#9ca3af", padding:"4px", display:"flex", borderRadius:"6px" }}>
            <X size={18} />
          </button>
        </div>
        <form onSubmit={handleSubmit} noValidate>
          <div className="pp-dialog-body">
            <div className="pp-form-grid-2">
              <F field="firstName" label="First Name" placeholder="First name" autoFocus />
              <F field="lastName"  label="Last Name"  placeholder="Last name"  />
            </div>
            <div className="pp-form-grid-2">
              <F field="email" label="Email" type="email" placeholder="Email address" />
              <F field="phone" label="Phone" type="tel"   placeholder="Phone number"  />
            </div>
            <F field="address" label="Address" placeholder="Street address" />
            <div className="pp-form-grid-5">
              <F field="city" label="City" placeholder="City" />
              <div>
                <label className="pp-field-label">State</label>
                <select value={form.state} onChange={e => set("state", e.target.value)}
                  className={`pp-field-input pp-select ${errors.state ? "error" : ""}`} style={{ paddingLeft: "11px" }}>
                  <option value="">State</option>
                  {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                {errors.state && <div className="pp-field-error">{errors.state}</div>}
              </div>
              <F field="zip" label="ZIP" placeholder="ZIP" />
            </div>
            <div>
              <label className="pp-field-label">About Your Building <span style={{ color: "#9ca3af" }}>(optional)</span></label>
              <textarea value={form.building} onChange={e => set("building", e.target.value)} rows={3} className="pp-field-input pp-textarea" placeholder="Describe your building…" />
            </div>
          </div>
          <div className="pp-dialog-foot">
            <button type="button" onClick={onClose} disabled={submitting} className="up-btn up-btn-outline">Close</button>
            <button type="submit" disabled={submitting} className="up-btn up-btn-dark">
              {submitting ? <><Loader size={13} style={{ animation:"upSpin .7s linear infinite" }} /> Submitting…</> : <><Send size={13} /> Submit</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ── Project Edit Dialog ── */
function ProjectEditDialog({ open, initial, onClose, onSave, saving }) {
  const [form, setForm] = useState({ name: "", street: "", city: "", state: "", zip: "" });
  const [errors, setErrors] = useState({});
  const [fetching, setFetching] = useState(false);

  useEffect(() => {
    if (!open || !initial?.id) return;
    setErrors({});
    setFetching(true);
    fetch(`${BASE}/api/projects/${initial.id}`, { credentials: "include" })
      .then(r => r.ok ? r.json() : null)
      .then(p => {
        if (p) setForm({ name: p.name || "", street: p.street || "", city: p.city || "", state: p.state || "", zip: p.zip || "" });
        else setForm({ name: initial.name || "", street: initial.street || "", city: initial.city || "", state: initial.state || "", zip: initial.zip || "" });
      })
      .catch(() => setForm({ name: initial.name || "", street: initial.street || "", city: initial.city || "", state: initial.state || "", zip: initial.zip || "" }))
      .finally(() => setFetching(false));
  }, [open, initial?.id]);

  if (!open) return null;

  function setField(field, val) { setForm(f => ({ ...f, [field]: val })); setErrors(e => ({ ...e, [field]: undefined })); }

  function handleSubmit(e) {
    e.preventDefault();
    const errs = {};
    if (!form.name.trim()) errs.name = "Required";
    if (Object.keys(errs).length) { setErrors(errs); return; }
    onSave(form);
  }

  return (
    <div className="pp-dialog-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="pp-dialog">
        <div className="pp-dialog-head">
          <div className="pp-dialog-title">Edit Project</div>
          <button type="button" onClick={onClose} style={{ background:"none", border:"none", cursor:"pointer", color:"#9ca3af", padding:"4px", display:"flex", borderRadius:"6px" }}><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} noValidate>
          <div className="pp-dialog-body">
            <div>
              <label className="pp-field-label">Project Name *</label>
              <input autoFocus disabled={fetching} className={`pp-field-input ${errors.name ? "error" : ""}`} value={form.name} onChange={e => setField("name", e.target.value)} placeholder="Project name" />
              {errors.name && <div className="pp-field-error">{errors.name}</div>}
            </div>
            <div>
              <label className="pp-field-label">Street Address</label>
              <input disabled={fetching} className="pp-field-input" value={form.street} onChange={e => setField("street", e.target.value)} placeholder="Street address" />
            </div>
            <div className="pp-form-grid-5">
              <div>
                <label className="pp-field-label">City</label>
                <input disabled={fetching} className="pp-field-input" value={form.city} onChange={e => setField("city", e.target.value)} placeholder="City" />
              </div>
              <div>
                <label className="pp-field-label">State</label>
                <select disabled={fetching} className="pp-field-input pp-select" style={{ paddingLeft:"11px" }} value={form.state} onChange={e => setField("state", e.target.value)}>
                  <option value="">State</option>
                  {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="pp-field-label">ZIP</label>
                <input disabled={fetching} className="pp-field-input" value={form.zip} onChange={e => setField("zip", e.target.value)} placeholder="ZIP" />
              </div>
            </div>
          </div>
          <div className="pp-dialog-foot">
            <button type="button" onClick={onClose} disabled={saving || fetching} className="up-btn up-btn-outline">Cancel</button>
            <button type="submit" disabled={saving || fetching} className="up-btn up-btn-dark">
              {saving ? <><Loader size={13} style={{ animation:"upSpin .7s linear infinite" }} /> Saving…</> : fetching ? <><Loader size={13} style={{ animation:"upSpin .7s linear infinite" }} /> Loading…</> : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ── Main ── */
export default function ProjectsPage() {
  useEffect(() => { injectLocal(); }, []);

  const [mode, setMode]           = useState("");
  const [primaryOrg, setPrimaryOrg] = useState(null);
  const [orgRole, setOrgRole]     = useState(null);
  const [rows, setRows]           = useState([]);
  const [members, setMembers]     = useState([]);
  const [features, setFeatures]   = useState(null);
  const [accessMode, setAccessMode] = useState(null); // "trial" | "paid" | "blocked" | null
  const [sp, setSp]               = useSearchParams();
  const [loading, setLoading]     = useState(true);
  const [view, setView]           = useState("card");
  const [q, setQ]                 = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [toDelete, setToDelete]   = useState(null);
  const [assigning, setAssigning] = useState(null);
  const [assignee, setAssignee]   = useState("");
  const [quoteDialogProjectId, setQuoteDialogProjectId] = useState(null);
  const [quoteSubmitting, setQuoteSubmitting] = useState(false);
  const [editingProject, setEditingProject] = useState(null); // { id, name, street, city, state, zip }
  const [editSaving, setEditSaving] = useState(false);
  const fileRef = useRef(null);
  const toast   = useToast();
  const { getEditorUrl } = useEditorLink();

  function notify(msg, variant = "success") {
    if (variant === "error") return toast.error(msg);
    if (variant === "info")  return toast.info?.(msg) || toast.success(msg);
    return toast.success(msg);
  }

  /* ── Auth / mode ── */
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${BASE}/api/auth/me`, { credentials: "include", cache: "no-store" });
        if (r.ok) {
          const me = await r.json();
          if (me.accountType === "organization") {
            const role = (me.primaryOrg?.role || "").toLowerCase();
            setPrimaryOrg(me.primaryOrg); setOrgRole(role);
            setMode(role === "owner" || role === "staff" ? "org_admin" : "org_invited");
          } else { setMode("individual"); }
        }
      } catch { setMode("individual"); }
    })();
  }, []);

  /* ── Refresh projects ── */
  async function refresh() {
    setLoading(true);
    try {
      const url = mode === "org_admin" ? `${BASE}/api/projects?scope=org` : `${BASE}/api/projects`;
      const res = await fetch(url, { credentials: "include" });
      const data = res.ok ? await res.json() : { items: [] };
      setRows(data.items || []);
      try {
        const ids = (data.items || []).map(i => i.id).join(",");
        if (ids) {
          const mres = await fetch(`${BASE}/api/purchase-plans/by-project?ids=${encodeURIComponent(ids)}`, { credentials: "include" });
          if (mres.ok) { const { map } = await mres.json(); setRows(prev => prev.map(p => ({ ...p, __pp: map?.[p.id] || null }))); }
          const qres = await fetch(`${BASE}/api/quotes/by-project?ids=${encodeURIComponent(ids)}`, { credentials: "include" });
          if (qres.ok) { const { map: qmap } = await qres.json(); setRows(prev => prev.map(p => ({ ...p, __quote: qmap?.[p.id] || null }))); }
        }
      } catch {}
    } finally { setLoading(false); }
  }

  /* ── Permission flags ── */
  const canCreate           = mode === "individual" || mode === "org_admin" || mode === "org_invited";
  const canRequestQuote     = mode === "individual" || (mode === "org_invited" && orgRole === "member");
  const canRequestPurchasePlan = mode === "individual" || mode === "org_admin" || (mode === "org_invited" && orgRole === "member");
  const canDelete           = mode === "individual" || mode === "org_admin";
  const canAssign           = mode === "org_admin";
  const canClone            = canCreate;

  useEffect(() => { if (!mode) return; refresh(); }, [mode]);

  useEffect(() => {
    window.addEventListener("project:assign", e => setAssigning(e.detail));
    return () => window.removeEventListener("project:assign", e => setAssigning(e.detail));
  }, []);

  useEffect(() => {
    const intent = sp.get("intent");
    if (intent === "new") { if (!mode) return; newProject(); sp.delete("intent"); setSp(sp, { replace: true }); }
  }, [sp, setSp, mode, rows, features]);

  /* ── Billing / features ── */
  useEffect(() => {
    if (!mode) return;
    (async () => {
      try {
        if (mode === "org_admin") {
          const r = await fetch(`${BASE}/api/org-billing/me`, { credentials: "include" });
          if (r.ok) {
            const d = await r.json();
            const feat = d.subscription?.plan?.features || [];
            setFeatures(Object.fromEntries(feat.map(f => [f.key, f.valueInt ?? f.valueString ?? f.valueJson ?? true])));
            setAccessMode(d.accessMode || null);
          }
        } else {
          const r = await fetch(`${BASE}/api/billing/me`, { credentials: "include" });
          if (r.ok) {
            const d = await r.json();
            setFeatures(d.features || d.subscription?.features || {});
            setAccessMode(d.accessMode || null);
          }
        }
      } catch {}
    })();
  }, [mode]);

  /* ── Members (for assign) ── */
  useEffect(() => {
    if (!canAssign) return;
    (async () => {
      try {
        const r = await fetch(`${BASE}/api/orgs/primary/members`, { credentials: "include" });
        if (r.ok) { const d = await r.json(); setMembers(d.items || []); }
      } catch {}
    })();
  }, [canAssign]);

  /* ── Quote handlers ── */
  function requestQuote(projectId) {
    if (mode === "individual") { setQuoteDialogProjectId(projectId); return; }
    if (mode === "org_invited" && orgRole === "member") {
      (async () => {
        try {
          const r = await fetch(`${BASE}/api/quotes/projects/${projectId}/request-org`, { method: "POST", credentials: "include" });
          if (!r.ok) throw new Error("Failed");
          notify("Quote request sent to your company.");
          await refresh();
        } catch { notify("Failed to request a quote.", "error"); }
      })();
    }
  }

  async function handleIndividualQuoteSubmit(formData) {
    if (!quoteDialogProjectId) return;
    setQuoteSubmitting(true);
    try {
      const res = await fetch(`${BASE}/api/quotes/projects/${quoteDialogProjectId}/quote-request`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ firstName: formData.firstName, lastName: formData.lastName, email: formData.email, phone: formData.phone, address: formData.address, city: formData.city, state: formData.state, zip: formData.zip, buildingDescription: formData.building }),
      });
      if (!res.ok) throw new Error("Submit failed");
      setQuoteDialogProjectId(null);
      notify("Quote request submitted! We'll be in touch via email soon.");
      await refresh();
    } catch { notify("Failed to submit quote request. Please try again.", "error"); }
    finally { setQuoteSubmitting(false); }
  }

  /* ── Purchase plan ── */
  async function purchasePlan(projectId) {
    if (!canRequestPurchasePlan) return;
    try {
      const res = await fetch(`${BASE}/api/purchase-plans/projects/${projectId}/request`, { method: "POST", credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      const data = await res.json();
      const base = `/dashboard/engineering-plans?projectId=${projectId}&from=projects`;
      if (data.state === "created")       { notify("Plan request submitted. Team will respond within 24 hours.", "info"); setTimeout(() => (window.location.href = base), 3000); return; }
      if (data.state === "has_pending")   { notify("A plan request is already under review.", "info"); setTimeout(() => (window.location.href = base), 3000); return; }
      if (data.state === "has_completed") { notify("A plan already exists. You can request rework.", "info"); setTimeout(() => (window.location.href = base + "&reworkHint=1"), 3000); return; }
      notify("Failed to send purchase plan request.", "error");
    } catch { notify("Failed to send purchase plan request.", "error"); }
  }

  /* ── Other actions ── */
  function importClick() {
    if (!canCreate) return;
    if (accessMode === "blocked") {
      notify("Your free trial has expired. Please upgrade your plan to import projects.", "error");
      setTimeout(() => { window.location.href = "/dashboard/subscription"; }, 2000);
      return;
    }
    fileRef.current?.click();
  }

  async function onImportFile(e) {
    const f = e.target.files?.[0]; if (!f) return;
    try {
      if (f.name.toLowerCase().endsWith(".mdps")) {
        const res = await fetch(`${BASE}/api/projects/import/encrypted`, { method: "POST", headers: { "Content-Type": "application/octet-stream" }, credentials: "include", body: await f.arrayBuffer() });
        if (!res.ok) throw new Error("Import failed");
        window.location.href = getEditorUrl((await res.json()).id);
      } else {
        const res = await fetch(`${BASE}/api/projects/import`, { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: await f.text() });
        if (!res.ok) throw new Error("Import failed");
        window.location.href = getEditorUrl((await res.json()).id);
      }
    } catch { alert("Failed to import project."); }
  }

  function newProject() {
    if (!canCreate) return;
    if (accessMode === "blocked") {
      notify("Your free trial has expired. Please upgrade your plan to create projects.", "error");
      setTimeout(() => { window.location.href = "/dashboard/subscription"; }, 2000);
      return;
    }
    if (mode === "individual") {
      const max = Number(features?.["projects.max"] ?? 5);
      if (rows.filter(r => r.status === "active").length >= max) {
        notify(`You've reached your project limit (${max}). Upgrade your plan for more projects.`, "error");
        setTimeout(() => { window.location.href = "/dashboard/subscription"; }, 2000);
        return;
      }
    }
    if (mode === "org_admin" || mode === "org_invited") {
      (async () => {
        try {
          const r = await fetch(`${BASE}/api/projects/limits?scope=org`, { credentials: "include" });
          if (r.ok) { const d = await r.json(); if (d.orgProjectsMax != null && d.orgProjectsUsed >= d.orgProjectsMax) { notify(`Organization has reached its project quota (${d.orgProjectsMax}).`, "error"); return; } }
        } catch {}
        window.location.href = getEditorUrl("new");
      })();
      return;
    }
    window.location.href = getEditorUrl("new");
  }

  async function onDeleteConfirmed() {
    const id = toDelete; setToDelete(null);
    await fetch(`${BASE}/api/projects/${id}`, { method: "DELETE", credentials: "include" });
    refresh();
  }

  async function cloneProject(projectId) {
    if (!canClone) return;
    if (accessMode === "blocked") {
      notify("Your free trial has expired. Please upgrade your plan to clone projects.", "error");
      setTimeout(() => { window.location.href = "/dashboard/subscription"; }, 2000);
      return;
    }
    try {
      const res = await fetch(`${BASE}/api/projects/${projectId}/clone`, { method: "POST", credentials: "include" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (data.error === "subscription_required") {
          notify(data.message || "Your free trial has expired. Please upgrade to continue.", "error");
          setTimeout(() => { window.location.href = "/dashboard/subscription"; }, 2000);
          return;
        }
        if (data.error === "org_project_limit" || data.error === "free_limit_reached") {
          notify(data.message || "Project limit reached.", "error");
          return;
        }
        throw new Error("Failed");
      }
      notify(`"${data.name}" created.`);
      await refresh();
    } catch { notify("Failed to clone project.", "error"); }
  }

  async function saveProjectEdit({ name, street, city, state, zip }) {
    if (!editingProject) return;
    setEditSaving(true);
    try {
      const res = await fetch(`${BASE}/api/projects/${editingProject.id}`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ name, address: { street, city, state, zip } }),
      });
      if (!res.ok) throw new Error("Failed");
      setEditingProject(null);
      await refresh();
    } catch { notify("Failed to save project details.", "error"); }
    finally { setEditSaving(false); }
  }

  async function assign(projectId, userIdOrNull) {
    if (!canAssign) return;
    await fetch(`${BASE}/api/projects/${projectId}`, { method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify({ assigneeUserId: userIdOrNull }) });
    setAssigning(null); setAssignee(""); refresh();
  }

  /* ── Filters / stats ── */
  const filteredProjects = useMemo(() => {
    let f = rows;
    const term = q.trim().toLowerCase();
    if (term) f = f.filter(r => (r.name || "").toLowerCase().includes(term));
    if (statusFilter !== "all") f = f.filter(r => r.status === statusFilter);
    return f;
  }, [rows, q, statusFilter]);

  const stats = { total: rows.length, active: rows.filter(r => r.status === "active").length, archived: rows.filter(r => r.status === "archived").length, draft: rows.filter(r => r.status === "draft").length };
  const maxProjects = Number(features?.["projects.max"] ?? 5);
  const isNearLimit = stats.active >= maxProjects * 0.8;
  const isPremium   = (features?.["projects.max"] ?? 5) > 5;

  /* ── Render ── */
  const isMember = mode === "org_invited" && orgRole === "member";
  const sharedViewProps = { rows: filteredProjects, loading, onOpen: id => (window.location.href = getEditorUrl(id)), onDelete: id => setToDelete(id), canDelete, canAssign, isIndividual: mode === "individual", canRequestQuote, canRequestPurchasePlan, onRequestQuote: requestQuote, onPurchasePlan: purchasePlan, isMember, canClone, onClone: cloneProject, onEdit: p => setEditingProject(p) };

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Header */}
      <div style={{ display:"flex", alignItems:"flex-end", justifyContent:"space-between", flexWrap:"wrap", gap:"12px", marginBottom:"24px" }}>
        <div>
          <div className="up-title">{mode === "org_admin" ? "Company Projects" : "My Projects"}</div>
          <div className="up-sub">
            {mode === "org_admin" ? `Managing projects for ${primaryOrg?.name || "your organization"}` : "Manage and organize your design projects"}
          </div>
        </div>
        <button className="up-btn up-btn-outline" onClick={refresh} disabled={loading}>
          <RefreshCw size={14} style={{ animation: loading ? "upSpin .7s linear infinite" : "none" }} /> Refresh
        </button>
      </div>

      {/* KPI row */}
      <div className="up-kpi-grid" style={{ marginBottom: "20px" }}>
        {[
          { accent:"#0c1118", val: stats.total,    lbl:"Total Projects" },
          { accent:"#10b981", val: stats.active,   lbl:"Active"         },
          { accent:"#f9ab14", val: stats.draft,    lbl:"Drafts"         },
          { accent:"#9ca3af", val: stats.archived, lbl:"Archived"       },
        ].map(k => (
          <div className="up-kpi" key={k.lbl}>
            <div className="up-kpi-accent" style={{ background: k.accent }} />
            <div className="up-kpi-val">{loading ? "—" : k.val}</div>
            <div className="up-kpi-lbl">{k.lbl}</div>
          </div>
        ))}
      </div>

      {/* Usage banner (individual only) */}
      {mode === "individual" && (
        <div className="pp-usage-banner" style={{ background: isNearLimit ? "rgba(249,171,20,.1)" : isPremium ? "rgba(16,185,129,.08)" : "rgba(59,130,246,.08)", border: `1px solid ${isNearLimit ? "rgba(249,171,20,.25)" : isPremium ? "rgba(16,185,129,.2)" : "rgba(59,130,246,.15)"}` }}>
          <div style={{ display:"flex", alignItems:"center", gap:"12px" }}>
            <div style={{ width:38, height:38, borderRadius:8, background: isPremium ? "rgba(16,185,129,.15)" : "rgba(59,130,246,.12)", display:"flex", alignItems:"center", justifyContent:"center" }}>
              {isPremium ? <Crown size={18} style={{ color:"#10b981" }} /> : <FolderOpen size={18} style={{ color:"#3b82f6" }} />}
            </div>
            <div>
              <div style={{ fontSize:"13.5px", fontWeight:600, color:"#0c1118" }}>
                {stats.active} of {maxProjects} projects used{isPremium && <span style={{ marginLeft:8, color:"#10b981", fontSize:12 }}>· Premium</span>}
              </div>
              <div style={{ fontSize:"12px", color: isNearLimit ? "#92400e" : "#6b7280", marginTop:2 }}>
                {isNearLimit ? "Approaching your project limit" : isPremium ? "Enjoying premium features" : "Upgrade for more projects"}
              </div>
            </div>
          </div>
          {!isPremium && <a href="/dashboard/subscription" className="up-btn up-btn-dark" style={{ textDecoration:"none" }}><Zap size={13} /> Upgrade</a>}
        </div>
      )}

      {/* Controls */}
      <div className="pp-controls">
        <div className="pp-controls-row">
          <div className="pp-search-wrap">
            <Search size={14} className="pp-search-icon" />
            <input className="pp-input" value={q} onChange={e => setQ(e.target.value)} placeholder="Search projects…" />
          </div>
          <div className="pp-filter-wrap">
            <Filter size={13} className="pp-filter-icon" />
            <select className="pp-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="draft">Draft</option>
              <option value="archived">Archived</option>
            </select>
          </div>
          <div style={{ flex:1 }} />
          {canCreate && (
            <>
              <button className="up-btn up-btn-dark" onClick={newProject}><Plus size={13} /> New Project</button>
              <button className="up-btn up-btn-outline" onClick={importClick}><Upload size={13} /> Import</button>
            </>
          )}
          <div className="pp-view-toggle">
            <button className={`pp-view-btn ${view==="card" ? "active":""}`} onClick={() => setView("card")} title="Card view"><Grid size={15} /></button>
            <button className={`pp-view-btn ${view==="table"? "active":""}`} onClick={() => setView("table")} title="Table view"><List size={15} /></button>
          </div>
        </div>
      </div>

      <input ref={fileRef} type="file" accept=".mdp,.mdps,application/json" style={{ display:"none" }} onChange={onImportFile} />

      {/* Content */}
      {view === "table" ? <ProjTableView {...sharedViewProps} /> : <ProjCardView {...sharedViewProps} />}

      {/* Assign modal */}
      {assigning && canAssign && (
        <div className="pp-modal-backdrop" onClick={e => e.target === e.currentTarget && setAssigning(null)}>
          <div className="pp-modal">
            <div className="pp-modal-title"><UserPlus size={16} /> Assign Project</div>
            <select value={assignee} onChange={e => setAssignee(e.target.value)}
              className="pp-field-input pp-select" style={{ width:"100%", paddingLeft:11, marginBottom:16 }}>
              <option value="">Unassigned</option>
              {members.map(m => <option key={m.userId} value={m.userId}>{(m.firstname||"")+" "+(m.lastname||"")} ({m.role})</option>)}
            </select>
            <div style={{ display:"flex", justifyContent:"flex-end", gap:"10px" }}>
              <button className="up-btn up-btn-outline" onClick={() => { setAssigning(null); setAssignee(""); }}>Cancel</button>
              <button className="up-btn up-btn-dark" onClick={() => assign(assigning, assignee || null)}>Save</button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal open={Boolean(toDelete)} title="Archive Project" message="This project will be archived." onCancel={() => setToDelete(null)} onConfirm={onDeleteConfirmed} />

      <QuoteRequestDialog open={!!quoteDialogProjectId} onClose={() => setQuoteDialogProjectId(null)} onSubmit={handleIndividualQuoteSubmit} submitting={quoteSubmitting} />

      <ProjectEditDialog open={!!editingProject} initial={editingProject} onClose={() => setEditingProject(null)} onSave={saveProjectEdit} saving={editSaving} />
    </div>
  );
}

/* ── Table view ── */
function ProjTableView({ rows, loading, onOpen, onDelete, canDelete, canAssign, canRequestQuote, canRequestPurchasePlan, onRequestQuote, onPurchasePlan, isMember, canClone, onClone, onEdit }) {
  if (loading) return <div className="up-panel"><div className="up-loading"><div className="up-spinner" /> Loading projects…</div></div>;
  return (
    <div className="pp-table-card">
      <div style={{ overflowX:"auto" }}>
        <table className="pp-table">
          <thead><tr>
            <th>Project</th><th>Status</th><th>Updated</th>
            {canAssign && <th>Assignee</th>}
            <th style={{ textAlign:"right" }}>Actions</th>
          </tr></thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={canAssign ? 5 : 4}><div className="up-empty"><FolderOpen size={36} style={{ color:"#d1d5db", margin:"0 auto" }} /><div className="up-empty-sub">No projects found</div></div></td></tr>
            ) : rows.map(p => (
              <ProjTableRow key={p.id} p={p} onOpen={onOpen} onDelete={onDelete} canDelete={canDelete} canAssign={canAssign} canRequestQuote={canRequestQuote} canRequestPurchasePlan={canRequestPurchasePlan} onRequestQuote={onRequestQuote} onPurchasePlan={onPurchasePlan} isMember={isMember} canClone={canClone} onClone={onClone} onEdit={onEdit} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ProjTableRow({ p, onOpen, onDelete, canDelete, canAssign, canRequestQuote, canRequestPurchasePlan, onRequestQuote, onPurchasePlan, isMember, canClone, onClone, onEdit }) {
  const [nameHovered, setNameHovered] = useState(false);
  const isLocked = isMember && !!p.lockedAt;
  return (
    <tr>
      <td>
        <div style={{ display:"flex", alignItems:"center", gap:"10px" }}>
          <div style={{ width:36, height:36, borderRadius:8, background:"#0c1118", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
            <FolderOpen size={16} style={{ color:"#f9ab14" }} />
          </div>
          <div>
            <div
              style={{ display:"inline-flex", alignItems:"center", gap:4, maxWidth:"100%" }}
              onMouseEnter={() => setNameHovered(true)}
              onMouseLeave={() => setNameHovered(false)}
            >
              <div className="pp-proj-name" title={p.name}>{p.name}</div>
              {nameHovered && (
                <button
                  className="pp-icon-btn"
                  style={{ padding:"2px 3px" }}
                  title="Edit project name & address"
                  onClick={e => { e.stopPropagation(); onEdit(p); }}
                >
                  <Edit2 size={11} />
                </button>
              )}
            </div>
            {p.address && <div className="pp-proj-addr" title={p.address}>{p.address}</div>}
            <div className="pp-proj-id">ID: {p.id.slice(0,8)}…</div>
          </div>
        </div>
      </td>
      <td>
        <div style={{ display:"flex", flexWrap:"wrap", gap:"4px", alignItems:"center" }}>
          <span className={statusBadgeClass(p.status)}>{fmtStatus(p.status)}</span>
          {p.__pp    && <span className="pp-pill pp-pill-plan">Plan</span>}
          {p.__quote && <span className="pp-pill pp-pill-quote">Quote</span>}
          {isLocked  && <span className="pp-pill pp-pill-lock" title="Design is locked — a quote or plan is active"><Lock size={8} style={{ marginRight:3 }} />Locked</span>}
        </div>
      </td>
      <td>
        <div style={{ display:"flex", alignItems:"center", gap:"5px", fontSize:"12px", color:"#6b7280" }}>
          <Calendar size={12} style={{ color:"#9ca3af" }} />
          {new Date(p.updatedAt).toLocaleDateString()}
        </div>
      </td>
      {canAssign && (
        <td>
          {p.assignee ? <div style={{ fontSize:"13px", color:"#374151" }}>{(p.assignee.firstname||"")+" "+(p.assignee.lastname||"")}</div>
                      : <span style={{ fontSize:"12px", color:"#d1d5db" }}>Unassigned</span>}
        </td>
      )}
      <td>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"flex-end", gap:"2px" }}>
          <button className="pp-icon-btn" disabled={p.status==="archived" || isLocked} onClick={() => p.status!=="archived" && !isLocked && onOpen(p.id)} title={isLocked ? "Project is locked" : "Open Project"}><Eye size={15} /></button>
          {canClone && <button className="pp-icon-btn" title="Clone Project" onClick={() => onClone(p.id)}><Copy size={14} /></button>}
          {p.status!=="archived" && canAssign && <button className="pp-icon-btn" title="Assign" onClick={() => window.dispatchEvent(new CustomEvent("project:assign",{detail:p.id}))}><UserPlus size={15} /></button>}
          {p.status!=="archived" && p.__pp?.url && <button className="pp-icon-btn" title="Download Plan" onClick={() => window.open(p.__pp.url,"_blank")}><MdDownload size={17} /></button>}
          {p.status!=="archived" && p.__quote?.url && <button className="pp-icon-btn" title="Download Quote" onClick={() => window.open(p.__quote.url,"_blank")}><MdDownload size={17} /></button>}
          {p.status!=="archived" && canRequestPurchasePlan && <button className="pp-icon-btn" title="Purchase Plan" onClick={() => onPurchasePlan(p.id)}><HiCurrencyDollar size={17} /></button>}
          {p.status!=="archived" && canRequestQuote && !isLocked && <button className="pp-icon-btn" title="Request Quote" onClick={() => onRequestQuote(p.id)}><FaTelegramPlane size={14} /></button>}
          {p.canDelete && <button className="pp-icon-btn" title="Delete" style={{ color:"#ef4444" }} onClick={() => onDelete(p.id)}><Trash2 size={14} /></button>}
        </div>
      </td>
    </tr>
  );
}

/* ── Card view ── */
function ProjCardView({ rows, loading, onOpen, onDelete, canDelete, canAssign, canRequestQuote, canRequestPurchasePlan, onRequestQuote, onPurchasePlan, isMember, canClone, onClone, onEdit }) {
  if (loading) return (
    <div className="pp-card-grid">
      {Array.from({length:8}).map((_,i) => (
        <div key={i} style={{ background:"#fff", border:"1px solid #e8ecf0", borderRadius:10, overflow:"hidden", opacity:.5 }}>
          <div style={{ aspectRatio:"16/9", background:"#f3f4f6" }} />
          <div style={{ padding:14 }}><div style={{ height:13, background:"#f3f4f6", borderRadius:4, marginBottom:8 }} /><div style={{ height:10, background:"#f3f4f6", borderRadius:4, width:"60%" }} /></div>
        </div>
      ))}
    </div>
  );
  if (rows.length === 0) return (
    <div className="up-panel"><div className="up-empty"><FolderOpen size={44} style={{ color:"#d1d5db", margin:"0 auto" }} /><div className="up-empty-sub">No projects found</div></div></div>
  );
  return (
    <div className="pp-card-grid">
      {rows.map(p => <ProjCard key={p.id} p={p} onOpen={onOpen} onDelete={onDelete} canDelete={canDelete} canAssign={canAssign} canRequestQuote={canRequestQuote} canRequestPurchasePlan={canRequestPurchasePlan} onRequestQuote={onRequestQuote} onPurchasePlan={onPurchasePlan} isMember={isMember} canClone={canClone} onClone={onClone} onEdit={onEdit} />)}
    </div>
  );
}

function ProjCard({ p, onOpen, onDelete, canDelete, canAssign, canRequestQuote, canRequestPurchasePlan, onRequestQuote, onPurchasePlan, isMember, canClone, onClone, onEdit }) {
  const [nameHovered, setNameHovered] = useState(false);
  const isArchived = p.status === "archived";
  const isLocked = isMember && !!p.lockedAt;
  return (
    <div className="pp-card">
      <div className="pp-card-thumb">
        {p.thumbnailUrl
          ? <img src={p.thumbnailUrl} alt={p.name} loading="lazy" onError={e => { e.currentTarget.src = "/img/project-placeholder.jpg"; }} />
          : <ImageIcon size={28} style={{ color:"#d1d5db" }} />}
        <div style={{ position:"absolute", top:8, right:8 }}>
          <div style={{ width:9, height:9, borderRadius:"50%", border:"2px solid #fff", background: p.status==="active" ? "#10b981" : p.status==="draft" ? "#f9ab14" : "#9ca3af" }} />
        </div>
        {/* Plan / Quote chips — top-left corner */}
        {(p.__pp || p.__quote) && (
          <div style={{ position:"absolute", top:8, left:8, display:"flex", gap:4, flexWrap:"wrap", zIndex:1 }}>
            {p.__pp && !p.__pp.url && <span className="pp-pill" style={{ background:"rgba(0,0,0,.6)", color:"#fff", backdropFilter:"blur(2px)" }}>Plan</span>}
            {p.__pp?.url && <a href={p.__pp.url} target="_blank" rel="noreferrer" className="pp-pill" style={{ textDecoration:"none", background:"rgba(0,0,0,.6)", color:"#fff", backdropFilter:"blur(2px)" }}>↓ Plan</a>}
            {p.__quote && <span className="pp-pill" style={{ background:"rgba(0,0,0,.6)", color:"#fff", backdropFilter:"blur(2px)" }}>Quote</span>}
          </div>
        )}
        {/* Lock overlay — members only */}
        {isLocked && (
          <div style={{ position:"absolute", inset:0, background:"rgba(12,17,24,0.5)", display:"flex", alignItems:"center", justifyContent:"center", backdropFilter:"blur(1px)" }}>
            <Lock size={24} style={{ color:"rgba(255,255,255,0.75)" }} />
          </div>
        )}
      </div>
      <div className="pp-card-body">
        <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:6, marginBottom:4 }}>
          <div style={{ minWidth:0 }}
               onMouseEnter={() => setNameHovered(true)}
               onMouseLeave={() => setNameHovered(false)}>
            <div style={{ display:"flex", alignItems:"center", gap:4 }}>
              <div className="pp-card-name" title={p.name} style={{ minWidth:0 }}>{p.name}</div>
              {nameHovered && (
                <button
                  className="pp-icon-btn"
                  style={{ flexShrink:0, padding:"2px 3px" }}
                  title="Edit project name & address"
                  onClick={e => { e.stopPropagation(); onEdit(p); }}
                >
                  <Edit2 size={11} />
                </button>
              )}
            </div>
            {p.address && <div className="pp-card-addr" title={p.address}>{p.address}</div>}
            <div className="pp-card-date">Last edited: {new Date(p.updatedAt).toLocaleDateString()}</div>
            {canAssign && (
              <div style={{ fontSize:"11.5px", color:"#9ca3af", marginTop:2 }}>
                {p.assignee ? `${p.assignee.firstname||""} ${p.assignee.lastname||""}`.trim() : "Unassigned"}
              </div>
            )}
          </div>
          <span className={statusBadgeClass(p.status)} style={{ flexShrink:0 }}>{fmtStatus(p.status)}</span>
        </div>

        <div style={{ display:"flex", gap:6, alignItems:"center", marginTop:8 }}>
          <button className="pp-open-btn" disabled={isArchived || isLocked} onClick={() => !isArchived && !isLocked && onOpen(p.id)}>
            {isArchived ? "Archived" : isLocked ? "Locked" : "Open"}
          </button>
          <div className="pp-card-actions">
            {canClone && <TooltipButton2 icon={Copy} tooltip="Clone Project" onClick={() => onClone(p.id)} variant="gray" />}
            {!isArchived && canAssign && <TooltipButton2 icon={UserPlus} onClick={() => window.dispatchEvent(new CustomEvent("project:assign",{detail:p.id}))} variant="gray" tooltip="Assign" />}
            {!isArchived && canRequestQuote && !isLocked && <TooltipButton2 icon={FaTelegramPlane} tooltip="Request Quote" onClick={() => onRequestQuote(p.id)} variant="gray" />}
            {!isArchived && canRequestPurchasePlan && <TooltipButton2 icon={HiCurrencyDollar} tooltip="Purchase Plan" onClick={() => onPurchasePlan(p.id)} variant="green" />}
            {!isArchived && p.canDelete && <TooltipButton2 icon={Trash2} tooltip="Delete" onClick={() => onDelete(p.id)} variant="red" />}
          </div>
        </div>
      </div>
    </div>
  );
}
